'use client';

import { ArrowLeft, CheckCircle2, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../../lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/Button';

/**
 * Autonomous Campaign Sidebar
 *
 * Focused on a single autonomous campaign refinement session:
 * - Campaign context (job + goal)
 * - Refinement progress
 * - Simple "Restart refinement" action
 */
export function KeenAISidebar({ campaignContext, onNewChat }) {
  const navigate = useNavigate();

  const jobTitle = campaignContext?.jobTitle || '';
  const goal = campaignContext?.goal || '';
  const questionNumber = campaignContext?.questionNumber || 0;
  const totalQuestions = campaignContext?.totalQuestions || 5;

  const progressRatio = totalQuestions > 0 ? Math.min(1, questionNumber / totalQuestions) : 0;
  const progressPercent = Math.round(progressRatio * 100);

  return (
    <div
      className="fixed z-10 top-0 bottom-0 start-0 flex flex-col items-stretch shrink-0 w-80 bg-white border-r border-gray-200 overflow-hidden"
      style={{ width: '320px' }}
    >
      {/* Sidebar Header */}
      <div className="flex flex-col gap-3 px-4 py-4 border-b border-gray-200">
        {/* Logo and Back Button */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/campaigns/autonomous/new')}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4 text-gray-600" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 via-purple-600 to-orange-500 flex items-center justify-center">
              <span className="text-white font-bold text-sm">A</span>
            </div>
            <span className="text-lg font-semibold text-gray-900">ALICE</span>
          </div>
        </div>

        {/* Restart Refinement Button */}
        <Button
          variant="primary"
          size="md"
          className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white border-0 shadow-sm font-medium rounded-lg"
          onClick={onNewChat}
        >
          <span>Restart Refinement</span>
        </Button>
      </div>

      {/* Sidebar Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {/* Campaign Context */}
        {campaignContext && (campaignContext.jobTitle || campaignContext.goal) && (
          <div className="mb-6 px-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3 tracking-wide">CAMPAIGN CONTEXT</h3>
            <div className="bg-gray-50 rounded-lg p-3 space-y-2 border border-gray-200">
              {campaignContext.jobTitle && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Job Title</p>
                  <p className="text-sm text-gray-900 font-medium">{campaignContext.jobTitle}</p>
                </div>
              )}
              {campaignContext.goal && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Goal</p>
                  <p className="text-sm text-gray-900">{campaignContext.goal}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Refinement Progress */}
        <div className="mb-6 px-2">
          <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3 tracking-wide">REFINEMENT PROGRESS</h3>
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-orange-500" />
                <span className="text-xs font-medium text-gray-700">
                  Question {Math.max(1, questionNumber)} of {totalQuestions}
                </span>
              </div>
              <span className="text-xs text-gray-500 capitalize">
                {campaignContext?.status || 'refining'}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <p className="text-[11px] text-gray-500">
              ALICE is asking targeted questions to clarify requirements before executing your campaign.
            </p>
          </div>
        </div>
      </div>

      {/* Sidebar Footer - AI Assistant Profile */}
      <div className="border-t border-gray-200 px-4 py-4">
        <div className="flex items-center gap-3">
          <Avatar className="w-10 h-10 border-2 border-gray-200">
            <AvatarImage
              src="/hero-character.jpeg"
              alt="AI Assistant"
            />
            <AvatarFallback className="bg-gradient-to-br from-purple-500 to-orange-500 text-white">
              AI
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900">AI Assistant</p>
            <p className="text-xs text-gray-500">Always here to help</p>
          </div>
        </div>
      </div>
    </div>
  );
}

